import { Calendar, CalendarClock, MapPin, MoreVertical, Pencil, Copy, Trash2, BookmarkPlus } from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { ItineraryActivity, RateHawkBookingMetadata, SupplierStatus } from '../models/activity.model';
import { ACTIVITIES } from '../models/activity-configs.model';
import { getActivityTime } from './timeline-utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ActivityRoomsGuests } from '../activities-card/ActivityFields';
import ActivityDetailView, { ActivityDetailData } from '../modals/ActivityDetailView';
import { UnsplashAttribution } from '@/components/ui/UnsplashAttribution';
import { SupplierStatusSelect } from '../activities-card/SupplierStatusSelect';

interface TimelineCardProps {
  activity: ItineraryActivity;
  /** Vista lato viaggiatore: nasconde i campi riservati al TA (es. costo) nel dettaglio. */
  isTraveler?: boolean;
  onEdit?: (activity: ItineraryActivity) => void;
  onDelete?: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onSaveToLibrary?: (activity: ItineraryActivity) => void;
  /** Stato fornitore: interno al TA, mai reso al viaggiatore. */
  showSupplierStatus?: boolean;
  onSupplierStatusChange?: (activity: ItineraryActivity, status: SupplierStatus | null) => void;
}

export const TimelineCard = ({ activity, isTraveler = false, onEdit, onDelete, onDuplicate, onSaveToLibrary, showSupplierStatus = false, onSupplierStatusChange }: TimelineCardProps) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  
  const time = getActivityTime(activity);
  // Extract only HH:mm from the time string
  const formattedTime = time !== '23:59' ? time.substring(0, 5) : '';
  
  const getActivityIcon = () => {
    const activityConfig = ACTIVITIES.find(a => a.id === activity.type);
    return activityConfig?.icon || <Calendar className="h-4 w-4" />;
  };

  const getActivityLabel = () => {
    const activityConfig = ACTIVITIES.find(a => a.id === activity.type);
    return activityConfig ? t(activityConfig.labelKey as any) : activity.type;
  };

  const getBadgeColor = () => {
    return 'bg-gray-100 text-gray-800';
  };

  // I media sono già normalizzati in MediaItem[] da BaseActivityClass.
  const firstImage = activity.media?.[0];
  const activityMetadata = activity.metadata as RateHawkBookingMetadata | undefined;

  return (
    <>
    <div 
      onClick={() => setIsOpen(true)}
      className={`flex-shrink-0 w-80 h-full rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:border-gray-300 hover:shadow-lg cursor-pointer overflow-hidden flex flex-col`}
    >
      {/* Image Section - icon placeholder when no image */}
      <div className="w-full h-64 bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center relative">
        {firstImage ? (
          <>
            <img
              src={firstImage.url}
              alt={firstImage.alt || activity.title}
              className="w-full h-full object-cover"
            />
            <UnsplashAttribution item={firstImage} />
          </>
        ) : (
          <div className="text-gray-300 [&>svg]:h-20 [&>svg]:w-20">
            {getActivityIcon()}
          </div>
        )}

        {/* Stato fornitore, in alto a sinistra: il click non deve aprire il dettaglio. */}
        {showSupplierStatus && onSupplierStatusChange && (
          <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
            <SupplierStatusSelect
              value={activity.supplier_status}
              onChange={(status) => onSupplierStatusChange(activity, status)}
            />
          </div>
        )}

        {/* 3-dot actions menu */}
        {(onEdit || onDelete || onDuplicate || onSaveToLibrary) && (
          <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-8 w-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow-sm transition-colors">
                  <MoreVertical className="h-4 w-4 text-gray-700" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(activity)} className="flex items-center gap-2">
                    <Pencil className="h-4 w-4" />
                    {t('common.edit')}
                  </DropdownMenuItem>
                )}
                {onDuplicate && (
                  <DropdownMenuItem onClick={() => onDuplicate(activity)} className="flex items-center gap-2">
                    <Copy className="h-4 w-4" />
                    {t('common.duplicate')}
                  </DropdownMenuItem>
                )}
                {onSaveToLibrary && (
                  <DropdownMenuItem onClick={() => onSaveToLibrary(activity)} className="flex items-center gap-2">
                    <BookmarkPlus className="h-4 w-4" />
                    {t('library.saveToLibraryAction')}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem onClick={() => onDelete(activity)} className="flex items-center gap-2 text-red-600 focus:text-red-600">
                    <Trash2 className="h-4 w-4" />
                    {t('common.delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <div className="p-4 flex-1 flex flex-col min-h-0">
        {/* Time and Type Badge */}
        <div className="flex items-center justify-between mb-3">
          {formattedTime && (
            <div className="text-sm font-semibold text-gray-700 flex items-center gap-1">
              <CalendarClock className="h-4 w-4" />
             {formattedTime}
            </div>
          )}
          <div className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 ${getBadgeColor()}`}>
            {getActivityIcon()}
            {getActivityLabel()}
          </div>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-base mb-2 line-clamp-2 text-gray-900">
          {activity.title}
        </h3>

        {/* Location */}
        {activity.address && (
          <div className="text-sm text-gray-600 mb-2 line-clamp-1">
            <MapPin className="h-4 w-4 inline-block mr-1" />
            {activity.address}
          </div>
        )}

        {/* Rooms & guests (accommodation only) */}
        <div className="text-sm text-gray-600 mb-2">
          <ActivityRoomsGuests metadata={activityMetadata} />
        </div>

        {/* Description */}
        {activity.description && (
          <div className="relative flex-1 min-h-0 mb-3 overflow-hidden">
            <div className="h-full overflow-y-auto">
              <div 
                className="text-sm text-gray-600"
                dangerouslySetInnerHTML={{ __html: activity.description }}
              />
            </div>
            {/* Fade effect at the bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
          </div>
        )}

        {/* Additional Info */}
        {(activity as any).address && !activity.location && (
          <div className="text-xs text-gray-500 pt-2 border-t border-gray-200 line-clamp-1">
            {(activity as any).address}
          </div>
        )}
      </div>
    </div>

    {/* Dettaglio attività (sola lettura) — card condivisa con Libreria e builder */}
    <ActivityDetailView
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      icon={getActivityIcon()}
      title={activity.title}
      activity={activity as ActivityDetailData}
      hidePrivateFields={isTraveler}
    />
    </>
  );
};
